import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet.markercluster";

import { useProjectSelection } from "../../state/ProjectSelectionContext";
import { bucketToColor, indicatorBucket, type IndicatorBucket } from "../../utils/indicatorBuckets";
import { useTheme } from "../../theme";
import type { ProjectMapPoint } from "./types";
import "./projectMap.css";

function defineIndicatorColor(value: unknown): string {
  // Keep consistent with src/utils/helpers.tsx, but be resilient:
  // - Some datasets store indicator *colors* as strings: "red" | "yellow" | "green" | "grey".
  // - Some store numeric status codes: 1/2/3.
  // - Some store hex/rgb values.

  // Preferred visible grey for "no data".
  const GREY = "#9CA3AF";

  if (value == null) return GREY;

  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (!s) return GREY;

    // pass through hex/rgb/hsl-ish strings
    if (s.startsWith("#") || s.startsWith("rgb") || s.startsWith("hsl"))
      return value;

    // common semantic status strings
    if (s === "green") return "#45BB47";
    if (s === "yellow" || s === "amber") return "#ffd13a";
    if (s === "red") return "#D53541";
    if (
      s === "grey" ||
      s === "gray" ||
      s === "na" ||
      s === "n/a" ||
      s === "none"
    )
      return GREY;

    // fall back: maybe numeric in string form
    const n2 = Number(s);
    if (!Number.isFinite(n2)) return GREY;
    value = n2;
  }

  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return GREY;
  if (n === 0) return GREY;
  if (n === 1) return "#D53541";
  if (n === 2) return "#ffd13a";
  if (n === 3) return "#45BB47";
  if (n <= 0.5) return GREY;
  if (n <= 1.5) return "#D53541";
  if (n <= 2.5) return "#ffd13a";
  return "#45BB47";
}

function getBucketForPoint(p: ProjectMapPoint, mode: string): IndicatorBucket {
  switch (mode) {
    case "schedule":
      return indicatorBucket((p as any).schedule_indicator_color);
    case "mission":
      return indicatorBucket((p as any).mission_indicator_color);
    case "finance":
      return indicatorBucket((p as any).finance_indicator_color);
    case "data":
      // DEV-851: blue Complete-state override for closed projects.
      if ((p as any)._isCompletedProject) return "completed";
      return indicatorBucket((p as any).data_reliability_color);
    case "summary":
    default:
      return indicatorBucket((p as any).summary_indicator_color);
  }
}

function getColorForPoint(p: ProjectMapPoint, mode: string): string {
  switch (mode) {
    case "schedule":
      return defineIndicatorColor(p.schedule_indicator_color);
    case "mission":
      return defineIndicatorColor(p.mission_indicator_color);
    case "finance":
      return defineIndicatorColor(p.finance_indicator_color);
    case "data":
      // DEV-851: blue Complete-state override for closed projects.
      if ((p as any)._isCompletedProject) return "#106DD5";
      return defineIndicatorColor(p.data_reliability_color);
    case "summary":
    default:
      return defineIndicatorColor(p.summary_indicator_color);
  }
}

const MARKER_SIZE_NORMAL = 26;
const MARKER_SIZE_SPIDER = 18;

// Persisted user preference: whether selecting/clicking a project should auto-zoom the map.
const LS_AUTO_ZOOM_KEY = "l4e_map_auto_zoom";

// Guardrails for auto-fit / focus zoom behavior.
// Keep these conservative so we don't zoom in "too far" on dense single-city collections.
const FIT_MAX_ZOOM = 11;
const FIT_SINGLE_POINT_MIN_ZOOM = 9;

function makeProjectDivIcon(color: string, pulse: boolean, sizePx: number, isMiniProject = false) {
  const cls = `l4e-marker ${pulse ? "pulse" : ""} ${isMiniProject ? "mini" : ""}`;
  // Use direct inline styles instead of CSS custom properties — Firefox doesn't
  // reliably resolve vars set on innerHTML injected by Leaflet's divIcon wrapper.
  //
  // Task #207 Phase 2: when isMiniProject=true, the .mini class flips
  // border-radius from 999px (circle) to 4px (rounded square). Same size
  // and color, distinct shape — users can tell FP vs MP at a glance
  // without us recoloring or repositioning markers.
  return L.divIcon({
    className: "",
    html: `<div class="${cls}" style="background:${color};width:${sizePx}px;height:${sizePx}px"></div>`,
    iconSize: [sizePx, sizePx],
    iconAnchor: [sizePx / 2, sizePx / 2],
  });
}

function makePieSvg(counts: Record<string, number>, size = 44) {
  // IMPORTANT: SVG arc commands cannot reliably render a full 360° slice when
  // the start and end points coincide (e.g., when *all* children are the same
  // color). In that case the "pie" can look transparent (only the ring shows).
  // So we special-case a single-color pie and render it as a filled circle.

  const entries = Object.entries(counts).filter(([, c]) => c > 0);
  const total = entries.reduce((a, [, c]) => a + c, 0) || 1;
  const r = size / 2;
  let start = 0;

  if (entries.length === 1) {
    const [onlyColor] = entries[0];
    const ring = `<circle cx="${r}" cy="${r}" r="${r - 1}" fill="none" stroke="currentColor" stroke-width="2" />`;
    return (
      `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
      `<circle cx="${r}" cy="${r}" r="${r}" fill="${onlyColor}" />` +
      ring +
      `</svg>`
    );
  }

  const paths = entries.map(([color, count]) => {
    const frac = count / total;
    const end = start + frac * Math.PI * 2;

    const x1 = r + r * Math.cos(start);
    const y1 = r + r * Math.sin(start);
    const x2 = r + r * Math.cos(end);
    const y2 = r + r * Math.sin(end);

    const large = end - start > Math.PI ? 1 : 0;

    const d = [
      `M ${r} ${r}`,
      `L ${x1} ${y1}`,
      `A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`,
      "Z",
    ].join(" ");

    start = end;
    return `<path d="${d}" fill="${color}" stroke="currentColor" stroke-width="1" />`;
  });

  const ring = `<circle cx="${r}" cy="${r}" r="${r - 1}" fill="none" stroke="currentColor" stroke-width="2" />`;
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${paths.join("")}${ring}</svg>`;
}

function makeClusterIconFromCounts(
  counts: Record<string, number>,
  total: number,
) {
  const svg = makePieSvg(counts, 44);

  return L.divIcon({
    className: "",
    html: `
      <div class="l4e-cluster">
        <div class="l4e-cluster-pie">${svg}</div>
        <div class="l4e-cluster-count">${total}</div>
      </div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
}

function getMarkerColorCached(
  m: any,
  pointsByGuid: Map<string, ProjectMapPoint>,
  mode: string,
): string {
  const cached = (m?.options as any)?.markerColor as string | undefined;
  if (cached && typeof cached === "string") return cached;

  const guid = String((m?.options as any)?.projectGuid || "");
  if (guid) {
    const p = pointsByGuid.get(guid);
    if (p) {
      const c = getColorForPoint(p, mode);
      try {
        (m.options as any).markerColor = c;
      } catch {
        // ignore
      }
      return c;
    }
  }

  return "#9CA3AF";
}

export interface ProjectMapProps {
  points: ProjectMapPoint[];
  /**
   * Invoked when a marker is clicked.
   * If kpiMode is provided, callers can use it to open the corresponding KPI tab.
   */
  onProjectClick: (projectGuid: string, kpiMode?: string) => void;
  initialCenter?: [number, number];
  initialZoom?: number;
  showColorBy?: boolean;
  selectedProjectGuid?: string;
  pulseProjectGuid?: string;
  /** If provided (and non-empty), projects not in the list will be dimmed on the map. */
  searchMatchGuids?: string[] | null;
}

export default function ProjectMap({
  points,
  onProjectClick,
  initialCenter = [20, 0],
  initialZoom = 2,
  showColorBy = true,
  selectedProjectGuid,
  pulseProjectGuid,
  searchMatchGuids,
}: ProjectMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { themeName } = useTheme();

  // Debug Mode is toggled via the App gear and persisted in localStorage.
  // Read it during render so rerenders driven by App state keep this current.
  const debugEnabled = (() => {
    try {
      const v = window?.localStorage?.getItem("l4e_debug_mode");
      return v === "1" || v === "true" || v === "on";
    } catch {
      return false;
    }
  })();

  const mapRef = useRef<L.Map | null>(null);
  const layersControlRef = useRef<L.Control.Layers | null>(null);
  const zoomControlRef = useRef<L.Control.Zoom | null>(null);
  const clusterRef = useRef<any>(null);
  // Bumped when the cluster group is (re)created so the marker-build effect re-runs.
  const [clusterVersion, setClusterVersion] = useState(0);

  /* ── Theme-aware tile layers ── */
  const darkTileRef  = useRef<L.Layer | null>(null);
  const lightTileRef = useRef<L.Layer | null>(null);
  const activeDefaultRef = useRef<L.Layer | null>(null);

  const { mapColorMode, setMapColorMode, legendBucketFilter, setLegendBucketFilter } = useProjectSelection();

  const [autoZoomToSelection, setAutoZoomToSelection] = useState<boolean>(() => {
    try {
      const v = window.localStorage.getItem(LS_AUTO_ZOOM_KEY);
      if (v == null) return true;
      return v === "true";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(
        LS_AUTO_ZOOM_KEY,
        autoZoomToSelection ? "true" : "false",
      );
    } catch {
      // ignore
    }
  }, [autoZoomToSelection]);

  const pointsByGuid = useMemo(() => {
    const m = new Map<string, ProjectMapPoint>();
    for (const p of points) m.set(String(p.project_guid), p);
    return m;
  }, [points]);

  const markerByGuidRef = useRef<Map<string, L.Marker>>(new Map());
  const originalLatLngByGuidRef = useRef<Map<string, L.LatLngLiteral>>(new Map());
  const lastSpiderfiedClusterRef = useRef<any>(null);
  const lastFitKeyRef = useRef<string>("");
  /** Suppress the next zoom-to-selection when the click originated from the map itself. */
  const skipNextZoomRef = useRef(false);

  // Stable fingerprint: prevent marker rebuilds when the `points` array
  // gets a new reference but the actual content hasn't changed.
  // Without this, any upstream recomputation (Data useMemo in App.tsx) that
  // produces an identical point set still triggers clearLayers → addLayers,
  // which destroys the spiderfied state and causes the "instant collapse" bug.
  //
  // Simple approach: a GUID-set key detects structural changes (add/remove/move).
  // When only colors change, we do an in-place icon swap instead of a full rebuild.
  const guidSetKey = useMemo(() => {
    const parts: string[] = [];
    for (const p of points) {
      const g = String((p as any).project_guid || "");
      const lat = Number((p as any).lat || 0).toFixed(6);
      const lng = Number((p as any).lng || 0).toFixed(6);
      parts.push(`${g}:${lat},${lng}`);
    }
    parts.sort();
    return parts.join("|");
  }, [points]);
  const prevGuidSetKeyRef = useRef<string>("");

  const searchMatchSet = useMemo(() => {
    if (
      !searchMatchGuids ||
      !Array.isArray(searchMatchGuids) ||
      searchMatchGuids.length === 0
    )
      return null;
    return new Set(searchMatchGuids.map((g) => String(g)));
  }, [searchMatchGuids]);

  const applyMarkerClasses = useCallback(() => {
    const selected = String(selectedProjectGuid || "");
    const pulse = String(pulseProjectGuid || "");
    const matchSet = searchMatchSet;

    markerByGuidRef.current.forEach((marker, guid) => {
      const root = marker.getElement();
      const inner =
        (root?.querySelector(".l4e-marker") as HTMLElement | null) || null;
      const el = inner || (root as any as HTMLElement | null);
      if (!el) return;

      el.classList.toggle(
        "l4e-marker--selected",
        !!selected && guid === selected,
      );
      // When a project is selected, show a continuous "epicenter" ripple effect.
      el.classList.toggle(
        "l4e-marker--ripple",
        !!selected && guid === selected,
      );
      el.classList.toggle("l4e-marker--dim", !!matchSet && !matchSet.has(guid));

      if (!!pulse && guid === pulse) {
        // One-shot pulse (used when grid -> map pans/zooms to a project).
        el.classList.add("l4e-marker--pulseonce");
        window.setTimeout(() => {
          const root2 = marker.getElement();
          const inner2 =
            (root2?.querySelector(".l4e-marker") as HTMLElement | null) || null;
          const el2 = inner2 || (root2 as any as HTMLElement | null);
          if (el2) el2.classList.remove("l4e-marker--pulseonce");
        }, 1700);
      }
    });
  }, [pulseProjectGuid, searchMatchSet, selectedProjectGuid]);

  // ---------------------------------------------------------------------------
  // IMPORTANT: Keep markercluster behavior stable across benign rerenders.
  // Selecting a project should NOT tear down clusters or collapse spiderfy.
  // We therefore keep some fast-changing values in refs.
  // ---------------------------------------------------------------------------
  const mapColorModeRef = useRef<string>(mapColorMode);
  const pointsByGuidRef = useRef<Map<string, ProjectMapPoint>>(pointsByGuid);
  const applyMarkerClassesRef = useRef<() => void>(applyMarkerClasses);
  const onProjectClickRef = useRef<(guid: string, kpiMode?: string) => void>(onProjectClick);

  useEffect(() => {
    mapColorModeRef.current = mapColorMode;
  }, [mapColorMode]);

  useEffect(() => {
    pointsByGuidRef.current = pointsByGuid;
  }, [pointsByGuid]);

  useEffect(() => {
    applyMarkerClassesRef.current = applyMarkerClasses;
  }, [applyMarkerClasses]);

  useEffect(() => {
    onProjectClickRef.current = onProjectClick;
  }, [onProjectClick]);

  useEffect(() => {
    if (mapRef.current) return;
    if (!containerRef.current) return;

    const map = L.map(containerRef.current, {
      zoomControl: false,
      attributionControl: true,
    });

    // Initial view will be refined via fitBounds once points load.
    map.setView([initialCenter[0], initialCenter[1]], initialZoom);

    const osm = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      },
    );

    const cartoDark = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      {
        attribution: "&copy; OpenStreetMap contributors &copy; CARTO",
        maxZoom: 20,
        subdomains: "abcd" as any,
      },
    );

    const cartoLight = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      {
        attribution: "&copy; OpenStreetMap contributors &copy; CARTO",
        maxZoom: 20,
        subdomains: "abcd" as any,
      },
    );

    const esriWorldStreet = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "Tiles &copy; Esri",
        maxZoom: 19,
      },
    );

    const esriNatGeo = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "Tiles &copy; Esri",
        maxZoom: 16,
      },
    );

    const esriImagery = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Tiles &copy; Esri", maxZoom: 18 },
    );

    const esriImageryLabels = L.tileLayer(
      "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Tiles &copy; Esri", maxZoom: 18, opacity: 1 },
    );

    const esriHybrid = L.layerGroup([esriImagery, esriImageryLabels]);

    // Store theme-aware tile refs for the theme-switch effect.
    darkTileRef.current  = esriHybrid;
    lightTileRef.current = cartoLight;

    // Pick the default basemap based on current theme.
    const isDark = document.documentElement.getAttribute("data-theme") !== "light";
    const defaultLayer = isDark ? esriHybrid : cartoLight;
    defaultLayer.addTo(map);
    activeDefaultRef.current = defaultLayer;

    const baseLayers: Record<string, L.Layer> = {
      "CartoDB.Positron": cartoLight,
      "CartoDB.DarkMatter": cartoDark,
      OpenStreetMap: osm,
      "Esri.WorldStreetMap": esriWorldStreet,
      "Esri.NatGeoWorldMap": esriNatGeo,
      "Esri.Hybrid": esriHybrid,
    };

    const layersCtl = L.control.layers(baseLayers, undefined, {
      position: "topleft",
      collapsed: true,
    });
    layersCtl.addTo(map);
    layersControlRef.current = layersCtl;

    const zoomCtl = L.control.zoom({ position: "bottomleft" });
    zoomCtl.addTo(map);
    zoomControlRef.current = zoomCtl;

    mapRef.current = map;

    return () => {
      try {
        map.remove();
      } catch {
        // ignore
      }
      mapRef.current = null;
      layersControlRef.current = null;
      zoomControlRef.current = null;
      clusterRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Swap default basemap when theme changes ── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const wantLight = themeName === "light";
    const target = wantLight ? lightTileRef.current : darkTileRef.current;
    const current = activeDefaultRef.current;
    if (!target || target === current) return;

    // Only auto-swap if the user hasn't manually picked a different layer
    // via the layers control. We check if the current default is still active.
    if (current && map.hasLayer(current)) {
      map.removeLayer(current);
      target.addTo(map);
      activeDefaultRef.current = target;
    }
  }, [themeName]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const t = window.setTimeout(() => {
      try {
        map.invalidateSize();
      } catch {
        // ignore
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [points.length]);

  // Ensure map is correctly sized after mount/remount.
  // When navigating away and back, the component remounts but points.length
  // is unchanged, so the effect above doesn't fire.  The ResizeObserver also
  // doesn't fire on initial mount.  This effect fires once after the initial
  // render (and a brief delay) to guarantee invalidateSize runs.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Use a short chain of timeouts — the container may not have its final
    // dimensions on the very first frame (CSS transitions, flex layout, etc.).
    const t1 = window.setTimeout(() => {
      try { map.invalidateSize({ animate: false }); } catch { /* ignore */ }
    }, 50);
    const t2 = window.setTimeout(() => {
      try { map.invalidateSize({ animate: false }); } catch { /* ignore */ }
    }, 300);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, []);  

  // Keep Leaflet's internal size cache in sync when the map container resizes
  // (e.g. split pane opened, dragged, or detail panel toggled).
  useEffect(() => {
    const el = containerRef.current;
    const map = mapRef.current;
    if (!el || !map) return;
    const ro = new ResizeObserver(() => {
      try { map.invalidateSize({ animate: false }); } catch { /* ignore */ }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (clusterRef.current) {
      try {
        map.removeLayer(clusterRef.current);
      } catch {
        // ignore
      }
      clusterRef.current = null;
    }

    const cg = (L as any).markerClusterGroup({
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: false,
      maxClusterRadius: 55,
      // Keep markers alive during zoom transitions (small dataset; prevents "vanish" glitches).
      removeOutsideVisibleBounds: false,
      iconCreateFunction: (cluster: any) => {
        const children = (cluster?.getAllChildMarkers?.() as any[]) || [];
        const counts: Record<string, number> = {};
        for (const m of children) {
          if (!m) continue; // guard against undefined entries
          const c = getMarkerColorCached(
            m,
            pointsByGuidRef.current,
            mapColorModeRef.current,
          );
          counts[c] = (counts[c] || 0) + 1;
        }
        return makeClusterIconFromCounts(counts, children.length);
      },
    });

    cg.on("clusterclick", (e: any) => {
      const cluster = e.layer;
      const childCount = cluster?.getChildCount?.() ?? 0;
      const currentZoom = map.getZoom();
      const maxZoom = map.getMaxZoom?.() ?? 18;

      // For small clusters or at high zoom, spiderfy directly (no zoom change).
      // For large clusters at low zoom, zoom into the cluster bounds first.
      if (childCount <= 8 || currentZoom >= maxZoom - 1) {
        cluster.spiderfy();
      } else {
        const bounds = cluster.getBounds?.();
        if (bounds && bounds.isValid?.()) {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: maxZoom - 1 });
        } else {
          cluster.spiderfy();
        }
      }
    });
    cg.on("spiderfied", (e: any) => {
      lastSpiderfiedClusterRef.current = e?.cluster || e?.layer || null;
    });
    cg.on("unspiderfied", () => {
      lastSpiderfiedClusterRef.current = null;
    });

    // Debug: show cluster composition on hover (count + slice breakdown) so we can
    // diagnose "transparent" pies without relying on console output.
    if (debugEnabled) {
      const buildClusterDebugText = (cluster: any) => {
        const children = (cluster?.getAllChildMarkers?.() as any[]) || [];
        const counts: Record<string, number> = {};
        let resolved = 0;
        let missingGuid = 0;
        let missingPoint = 0;

        for (const m of children) {
          const guid = String((m?.options as any)?.projectGuid || "");
          if (!guid) missingGuid += 1;
          if (guid && !pointsByGuidRef.current.get(guid)) missingPoint += 1;

          const c = getMarkerColorCached(
            m,
            pointsByGuidRef.current,
            mapColorModeRef.current,
          );
          counts[c] = (counts[c] || 0) + 1;
          resolved += 1;
        }

        const parts = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .map(([c, n]) => `${c}:${n}`)
          .join(", ");

        const total = children.length;
        const detail = parts || "(no colors)";
        const miss =
          missingGuid || missingPoint
            ? ` | missingGuid:${missingGuid} missingPoint:${missingPoint}`
            : "";
        return `mode:${mapColorModeRef.current} | count:${total} | resolved:${resolved} | ${detail}${miss}`;
      };

      cg.on("clustermouseover", (e: any) => {
        try {
          const layer = e?.layer;
          if (!layer) return;
          const text = buildClusterDebugText(layer);
          if (layer.getTooltip && layer.getTooltip()) {
            // Update existing tooltip content if present
            (layer as any).setTooltipContent?.(text);
          } else {
            layer.bindTooltip?.(text, {
              sticky: true,
              direction: "top",
              offset: [0, -10],
              opacity: 0.95,
              className: "l4e-debug-tooltip",
            });
          }
          const at =
            e?.latlng || (layer.getLatLng ? layer.getLatLng() : undefined);
          if (at) layer.openTooltip?.(at);
        } catch {
          // ignore
        }
      });

      cg.on("clustermouseout", (e: any) => {
        try {
          e?.layer?.closeTooltip?.();
        } catch {
          // ignore
        }
      });
    }

    // Keep exploded markers compact to reduce overlap, while normal markers stay readable.
    cg.on("spiderfied", (e: any) => {
      const markers: any[] =
        (e?.cluster?.getAllChildMarkers?.() as any[]) || [];
      for (const m of markers) {
        if (!m) continue; // guard against undefined entries after clearLayers race
        const color = getMarkerColorCached(
          m,
          pointsByGuidRef.current,
          mapColorModeRef.current,
        );
        try {
          // Task #207 Phase 2: preserve mini-project shape across spiderfy.
          const isMp = !!(m.options as any).isMiniProject;
          m.setIcon(makeProjectDivIcon(color, false, MARKER_SIZE_SPIDER, isMp));
          (m.options as any).markerColor = color;
        } catch {
          // ignore
        }
      }
      window.setTimeout(() => applyMarkerClassesRef.current?.(), 0);
    });

    cg.on("unspiderfied", (e: any) => {
      const markers: any[] =
        (e?.cluster?.getAllChildMarkers?.() as any[]) || [];
      for (const m of markers) {
        if (!m) continue; // guard against undefined entries after clearLayers race
        const color = getMarkerColorCached(
          m,
          pointsByGuidRef.current,
          mapColorModeRef.current,
        );
        try {
          // Task #207 Phase 2: preserve mini-project shape on unspiderfy.
          const isMp = !!(m.options as any).isMiniProject;
          m.setIcon(makeProjectDivIcon(color, false, MARKER_SIZE_NORMAL, isMp));
          (m.options as any).markerColor = color;
        } catch {
          // ignore
        }
      }
      window.setTimeout(() => applyMarkerClassesRef.current?.(), 0);
    });

    // GUARD: Monkey-patch methods that can crash after clearLayers() nulls __parent.
    // The chain is: showMarker → hasLayer(marker.__parent) → L.Util.stamp(undefined) → crash.
    // This can happen via multiple paths: _animationEnd (CSS transitionend), _onDragEnd
    // (map pan finishes), moveend callbacks, etc.  We guard both the root cause
    // (showMarker) and the most common caller (_animationEnd) for belt-and-suspenders safety.
    const origShowMarker = (cg as any).showMarker;
    if (typeof origShowMarker === 'function') {
      (cg as any).showMarker = function (this: any, marker: any) {
        if (!marker?.__parent) return;  // marker was cleared — nothing to show
        return origShowMarker.call(this, marker);
      };
    }

    const origAnimEnd = (cg as any)._animationEnd;
    if (typeof origAnimEnd === 'function') {
      (cg as any)._animationEnd = function (this: any) {
        try {
          return origAnimEnd.call(this);
        } catch {
          // Swallow the stamp(undefined) crash — cluster was cleared mid-animation.
        }
      };
    }

    clusterRef.current = cg;
    map.addLayer(cg);
    // Force the marker-build effect to re-run and populate the new cluster group.
    // Without this, recreating the cluster group (e.g. debugEnabled toggle) leaves
    // markers orphaned on the old (removed) group → invisible markers.
    prevGuidSetKeyRef.current = "";
    setClusterVersion((v) => v + 1);

    return () => {
      try {
        map.removeLayer(cg);
      } catch {
        // ignore
      }
    };
  }, [debugEnabled]);

  // Build (or rebuild) markers when the point set or color mode changes.
  // Selection/pulse/search are applied via CSS classes to avoid marker rebuilds (prevents flashes).
  //
  // Two-level fingerprint strategy:
  //   structureFingerprint (guid + coordinates) → changed? Full rebuild.
  //   colorFingerprint (indicator colors)       → changed? Lightweight in-place icon swap.
  // This ensures KPI color updates re-color markers WITHOUT destroying cluster/spiderfy state.
  //
  // Depends directly on `points` so the effect fires whenever the upstream Data
  // recomputes (KPI scoring changes, version bumps, etc.).
  // Uses `guidSetKey` to decide between:
  //   - Full rebuild: when projects are added/removed/moved (guidSetKey changed)
  //   - In-place icon swap: when only colors changed (guidSetKey same)
  useEffect(() => {
    const cg = clusterRef.current;
    if (!cg) return;

    const structureChanged = prevGuidSetKeyRef.current !== guidSetKey;

    // Structure unchanged — lightweight in-place color update.
    // Preserves cluster/spiderfy state.
    if (!structureChanged && markerByGuidRef.current.size > 0) {
      console.debug("[ProjectMap] In-place color update:", points.length, "points");
      markerByGuidRef.current.forEach((marker, guid) => {
        const p = pointsByGuid.get(guid);
        if (!p) return;
        const color = getColorForPoint(p, mapColorMode);
        try {
          // Task #207 Phase 2: read the MP flag from the point so an
          // in-place color refresh keeps the rounded-square shape.
          marker.setIcon(makeProjectDivIcon(color, false, MARKER_SIZE_NORMAL, !!p._isMiniProject));
          (marker.options as any).markerColor = color;
        } catch { /* ignore */ }
      });
      cg.refreshClusters?.();
      window.setTimeout(() => applyMarkerClassesRef.current?.(), 0);
      return;
    }

    // Structure changed — full marker rebuild required.
    prevGuidSetKeyRef.current = guidSetKey;
    console.debug("[ProjectMap] Full marker rebuild:", points.length, "points, guidSetKey changed:", structureChanged);

    // Detach the cluster group from the map BEFORE clearing.
    // MarkerCluster registers 'animationend' and 'moveend' callbacks that
    // call showMarker → map.hasLayer(layer.__parent).  After clearLayers(),
    // __parent is nulled, so hasLayer(undefined) → stamp(undefined) → crash.
    // Removing from the map first cancels pending animation callbacks.
    const map = mapRef.current;
    try { cg.unspiderfy?.(); } catch { /* ignore */ }
    if (map) { try { map.removeLayer(cg); } catch { /* ignore */ } }
    cg.clearLayers();
    markerByGuidRef.current.clear();
    originalLatLngByGuidRef.current.clear();

    for (const p of points) {
      const guid = String(p.project_guid);
      const color = getColorForPoint(p, mapColorMode);
      console.debug(`[ProjectMap] Marker ${guid.slice(0,8)}… color=${color} (raw: summary=${(p as any).summary_indicator_color}, schedule=${(p as any).schedule_indicator_color})`);

      // Defensive: skip invalid coordinates.
      const lat = Number((p as any).lat);
      const lng = Number((p as any).lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) continue;

      // Task #207 Phase 2: MP markers get a rounded-square shape so
      // they're visually distinguishable from FP markers (circles). The
      // flag is also stashed in marker.options so the spiderfy /
      // unspiderfy refresh paths can preserve the shape on icon swap.
      const isMp = !!p._isMiniProject;
      const marker = L.marker([lat, lng], {
        icon: makeProjectDivIcon(color, false, MARKER_SIZE_NORMAL, isMp),
      } as any);
      (marker.options as any).isMiniProject = isMp;

      // Lightweight hover label for discoverability.
      // (Leaflet tooltips are much cheaper than popups and work well with clustering.)
      marker.bindTooltip(String((p as any).name || ""), {
        direction: "top",
        offset: [0, -8],
        opacity: 0.92,
        sticky: true,
      } as any);

      (marker.options as any).projectGuid = guid;
      (marker.options as any).markerColor = color;
      (marker.options as any).originalLat = lat;
      (marker.options as any).originalLng = lng;
      originalLatLngByGuidRef.current.set(guid, { lat, lng });
      // NOTE:
      // Only stop propagation on the marker *click*.
      // Stopping/`preventDefault()` on mousedown/mouseup can interfere with Leaflet's
      // internal click synthesis (especially after cluster spiderfy + pan), which can
      // cause seemingly "dead" markers until the map receives a neutral click.
      const stopClickBubble = (ev: any) => {
        try {
          ev?.originalEvent?.stopPropagation?.();
        } catch {
          // ignore
        }
      };
      marker.on("click", (ev: any) => {
        stopClickBubble(ev);
        // The user clicked directly on the map — skip the zoom-to-selection effect
        // that would otherwise fire when selectedProjectGuid updates in context.
        skipNextZoomRef.current = true;
        // KPI-aware map click:
        // if the map is currently coloring by Finance/Mission/etc, clicking a marker
        // should open that same KPI tab for the selected project.
        onProjectClickRef.current?.(guid, mapColorModeRef.current);
        // Re-spiderfy the last spiderfied cluster so it stays open after opening a project.
        window.setTimeout(() => {
          try {
            const c = lastSpiderfiedClusterRef.current;
            if (c && typeof c.spiderfy === "function") c.spiderfy();
          } catch {
            // ignore
          }
        }, 0);
      });

      markerByGuidRef.current.set(guid, marker);
      cg.addLayer(marker);
    }

    // Re-attach the cluster group to the map now that fresh markers are in.
    if (map) { try { map.addLayer(cg); } catch { /* ignore */ } }
    cg.refreshClusters?.();
    // Let Leaflet create marker DOM before applying CSS classes.
    window.setTimeout(() => applyMarkerClassesRef.current?.(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, mapColorMode, clusterVersion]);

  // Self-healing: detect marker/point count mismatch and force a rebuild.
  // Triggers on document visibility change (tab refocus) and on a periodic
  // safety check.  This catches any edge case where markers become orphaned
  // (e.g. cluster group recreation, Leaflet internal state corruption).
  useEffect(() => {
    const heal = () => {
      const cg = clusterRef.current;
      if (!cg || points.length === 0) return;
      const clusterCount = cg.getLayers?.()?.length ?? 0;
      if (clusterCount === 0 && points.length > 0) {
        console.warn(
          `[ProjectMap] Self-heal: cluster has 0 layers but ${points.length} points expected. Forcing rebuild.`
        );
        prevGuidSetKeyRef.current = "";
        setClusterVersion((v) => v + 1);
      }
    };

    // Check on tab re-focus (common scenario: user switches tabs, comes back)
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Small delay to let Leaflet settle after tab becomes visible
        window.setTimeout(heal, 300);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Periodic safety net — every 15 seconds, verify markers are present
    const interval = window.setInterval(heal, 15_000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [points.length]); // Re-subscribe when point count changes

  // Auto-fit view to the current collection's projects.
  // This runs when the point set changes (eg, switching collections) and keeps the map
  // zoomed in as much as possible without pushing any project markers outside the view.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const latlngs: L.LatLngExpression[] = [];
    const keyParts: string[] = [];

    for (const p of points) {
      const guid = String((p as any).project_guid || (p as any).guid || "");
      const lat = Number((p as any).lat);
      const lng = Number((p as any).lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) continue;
      // Ignore placeholder / invalid coordinates that often mean "unknown".
      if (lat === 0 && lng === 0) continue;
      latlngs.push([lat, lng]);
      keyParts.push(`${guid}:${lat.toFixed(6)},${lng.toFixed(6)}`);
    }

    if (latlngs.length === 0) return;

    // Prevent refit loops on cosmetic changes (eg Color By changes).
    const fitKey = keyParts.sort().join("|");
    if (fitKey && fitKey === lastFitKeyRef.current) return;
    lastFitKeyRef.current = fitKey;

    try {
      map.invalidateSize();
    } catch {
      // ignore
    }

    // DEV-920 Layer 2 follow-up. Wrap setView/fitBounds in try/catch
    // to swallow a known Leaflet + markercluster race: when `points`
    // shrinks (e.g., after a collection-filter change narrows project
    // count to 1), setView triggers a `moveend` event that fires
    // markercluster.showMarker(marker) where `marker` may be a stale/
    // undefined reference from the just-removed cluster. The marker
    // cluster's hasLayer() then throws "Cannot use 'in' operator to
    // search for '_leaflet_id' in undefined" — and because this happens
    // during a useEffect commit, React's render loop crashes the
    // entire page tree (escaped past any error boundary in the
    // PortfolioPage subtree). The catch here keeps the map in a sane-
    // enough state (the user can still pan/zoom; worst case the view
    // doesn't auto-fit) and lets the rest of the page render normally.
    // Real fix is upstream marker-lifecycle hygiene; tracked separately.
    try {
      if (latlngs.length === 1) {
        // Clamp zoom so we don't dive too deep when all projects are in one place.
        const z = Math.min(
          FIT_MAX_ZOOM,
          Math.max(map.getZoom(), FIT_SINGLE_POINT_MIN_ZOOM),
        );
        map.setView(latlngs[0] as any, z);
        return;
      }

      const bounds = L.latLngBounds(latlngs as any);
      if (!bounds.isValid()) return;

      map.fitBounds(bounds, {
        padding: [56, 56],
        maxZoom: FIT_MAX_ZOOM,
      } as any);
    } catch (e) {
      console.warn('[ProjectMap] setView/fitBounds threw — likely markercluster stale-marker race; continuing.', e);
    }
  }, [points]);

  useEffect(() => {
    const t = window.setTimeout(() => applyMarkerClasses(), 0);
    return () => window.clearTimeout(t);
  }, [applyMarkerClasses]);

  // When a project is selected from the grid (or any external source), pan/zoom
  // the map to that marker. Uses MarkerClusterGroup's zoomToShowLayer to expand
  // clusters as needed, then centers on the original coordinates.
  //
  // If the selection originated from a map click, skip the zoom (the marker is
  // already visible and zooming would fight with spiderfy/cluster state).
  useEffect(() => {
    const map = mapRef.current;
    const cg = clusterRef.current;
    const guid = String(selectedProjectGuid || '');
    if (!map || !cg || !guid) return;
    if (!autoZoomToSelection) return;

    // Selection originated from a map click — marker is already visible.
    if (skipNextZoomRef.current) {
      skipNextZoomRef.current = false;
      return;
    }

    // Small delay: if markers were just rebuilt (e.g. legend filter cleared),
    // the ref map may not yet be populated. Give the rebuild effect time to run.
    const zoomTimer = window.setTimeout(() => {
      const marker = markerByGuidRef.current.get(guid);
      if (!marker) return;

      // CRITICAL: tell Leaflet about the current container size before panning.
      // Without this, setView calculates the "center" based on stale dimensions
      // (e.g. before the split pane opened/resized), placing the marker off-center.
      try { map.invalidateSize({ animate: false }); } catch { /* ignore */ }

      // Use the original (non-spiderfied) coordinates for the final pan.
      const orig = originalLatLngByGuidRef.current.get(guid);

      const panToMarker = () => {
        try {
          const ll = orig ? (orig as any) : marker.getLatLng?.();
          if (!ll) return;
          // After zoomToShowLayer has set the zoom level needed to uncluster,
          // just pan to center — do NOT override the zoom with a cap, as that
          // could re-cluster the marker we just unclustered.
          const currentZoom = map.getZoom();
          // Only bump the zoom if we're far out; never zoom back down.
          const minZoom = Math.max(currentZoom, 10);
          map.setView(ll, minZoom, { animate: true } as any);
        } catch {
          // ignore
        }
      };

      try {
        // zoomToShowLayer zooms the map until the marker leaves its cluster.
        // The callback fires once the marker is individually visible.
        if (typeof (cg as any).zoomToShowLayer === 'function') {
          (cg as any).zoomToShowLayer(marker, () => panToMarker());
        } else {
          panToMarker();
        }
      } catch {
        panToMarker();
      }
    }, 50);

    return () => window.clearTimeout(zoomTimer);
  }, [selectedProjectGuid, autoZoomToSelection]);


  return (
    <div className="l4e-map-wrap" style={{ position: "relative" }}>
      {showColorBy && (
        <div className="l4e-map-toolbar">
          <div className="l4e-map-toolbar-row">
            <label>
              Color by
              <select
                value={mapColorMode}
                onChange={(e) => {
                  const next = e.target.value as any;
                  setMapColorMode(next);
                  // Reset legend filter when switching KPI mode to avoid confusion.
                  setLegendBucketFilter(null);
                }}
              >
                <option value="summary">Summary</option>
                <option value="schedule">Schedule</option>
                <option value="finance">Budget</option>
                <option value="mission">Mission</option>
                <option value="data">Data</option>
              </select>
            </label>
          </div>

          <div className="l4e-map-toolbar-row">
            <label
              className="l4e-map-toggle"
              title="When enabled, selecting a project will zoom the map to it."
            >
              <input
                type="checkbox"
                checked={autoZoomToSelection}
                onChange={(e) => setAutoZoomToSelection(e.target.checked)}
              />
              <span>Auto‑zoom</span>
            </label>
          </div>

          <div className="l4e-map-legend" role="group" aria-label="Legend filter">
            {([
              { bucket: "green", label: "Good" },
              { bucket: "yellow", label: "Watch" },
              { bucket: "red", label: "At risk" },
              { bucket: "grey", label: "No data" },
            ] as { bucket: IndicatorBucket; label: string }[]).map((it) => {
              const active = legendBucketFilter === it.bucket;
              return (
                <button
                  key={it.bucket}
                  type="button"
                  className={active ? "active" : ""}
                  onClick={() => {
                    const next = legendBucketFilter === it.bucket ? null : it.bucket;
                    setLegendBucketFilter(next);
                  }}
                  title={active ? "Clear filter" : `Filter: ${it.label}`}
                  aria-pressed={active}
                >
                  <span
                    className="dot"
                    style={{ background: bucketToColor(it.bucket) }}
                    aria-hidden="true"
                  />
                  <span className="label">{it.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div ref={containerRef} style={{ height: "100%", width: "100%" }} />
    </div>
  );
}
